// angular
import { NgClass, NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, effect, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ContactDialogComponent } from '@app/core/dialogs/contact-dialog/contact-dialog.component';
import { BeginPageSliderComponent } from '@app/core/components/begin-page-slider/begin-page-slider.component';
import { HomeSupplierCardComponent } from '@app/core/components/home-supplier-card/home-supplier-card.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
import { ContactInfoDialogComponent } from '@app/core/dialogs/contact-info-dialog/contact-info-dialog.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
// types & envs
import { ContactDialogItem } from '@app/core/dialogs/contact-dialog/shared/contact-dialog-item.type';
import { environment } from '@environments/environment';
import { ContactInfo } from '@app/+about/shared/types/contact-info.type';
// pipes & interfaces
import { IPortfolioHeroData } from '@app/+portfolio/shared/interfaces/portfolio-hero-data.interface';
import { IBusiness } from '@app/+suppliers/shared/interfaces/business.interface';
import { HasAccessDirective } from '@app/shared/directives/has-access.directive';
import { Roles } from '@app/shared/enums/roles.enum';

@Component({
  selector: 'pwj-hero-section',
  standalone: true,
  imports: [
    NgOptimizedImage,
    TranslateModule,
    BeginPageSliderComponent,
    HomeSupplierCardComponent,
    NgClass,
    ButtonComponent,
    HasAccessDirective,
  ],
  templateUrl: './hero-section.component.html',
  styleUrl: './hero-section.component.scss',
})
export class HeroSectionComponent {
  public baseUrl: string = environment.baseUrl.base;
  public roles = Roles;
  public supplierData!: IBusiness;
  // contact dialog data
  private _contactData!: ContactDialogItem[];

  // inputs
  heroData = input.required<IPortfolioHeroData>();
  contactInfo = input<ContactInfo[]>();

  // dependencies
  private readonly _dialogService = inject(DialogService);
  constructor() {
    effect(() => {
      const heroData = this.heroData();
      this.setHeroData(heroData);
      this.setContactUsData(heroData);
    });
  }

  public setHeroData(heroData: IPortfolioHeroData) {
    this.supplierData = {
      title: heroData?.title,
      country: heroData?.country,
      mobile: heroData?.mobile,
      logo: heroData?.logo,
      owner: heroData?.owner,
      categories: heroData?.categories,
    };
  }

  public setContactUsData(heroData: IPortfolioHeroData) {
    // set contact data
    this._contactData = [
      {
        id: 1,
        description: 'contactUsDialog.voiceCallWithSupplier',
        buttonIcon: 'mobile',
        buttonText: 'contactUsDialog.call',
        link: heroData?.mobile
          ? `tell:${heroData?.mobile?.prefix}${heroData?.mobile?.mobile_number}`
          : undefined,
      },
      {
        id: 2,
        description: 'contactUsDialog.chatOnWhatsApp',
        buttonIcon: 'phone',
        buttonText: 'contactUsDialog.whatsApp',
        link: heroData?.mobile
          ? `https://wa.me/${heroData?.mobile?.prefix}${
              heroData?.mobile?.mobile_number
            }`
          : undefined,
      },

      {
        id: 3,
        description: 'contactUsDialog.viewTheCompanyWebsite',
        buttonIcon: 'globe',
        buttonText: 'contactUsDialog.viewSite',
        link: heroData?.website_url
          ? `https://${heroData?.website_url}`
          : undefined,
        linkTarget: '_blank',
      },
    ];
  }

  public openContactUsBox() {
    const dialogRef = this._dialogService.open(ContactDialogComponent, {
      height: 'none',
      data: { title: 'global.contact', contactsData: this._contactData },
    });
    dialogRef.afterClosed().subscribe(res => {});
  }

  public openBusinessInfoBox() {
    const dialogRef = this._dialogService.open(ContactInfoDialogComponent, {
      width: 'none',
      height: 'none',
      data: {
        title: 'global.contact',
        contactInfo: this.contactInfo(),
      },
    });
    dialogRef.afterClosed().subscribe(res => {});
  }
}
